import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { inr, num, pnlColor } from "@/lib/format";
import { toast } from "sonner";
import { ShieldAlert, Zap, Power, RotateCcw } from "lucide-react";
import { visibleInterval } from "@/lib/poll";

const SEV = { high: "bg-[#FF3B30]/15 text-[#FF6b62]", critical: "bg-[#FF3B30]/20 text-[#FF6b62]", medium: "bg-[#FFCC00]/15 text-[#FFCC00]", low: "bg-[#94A3B8]/15 text-slate-300" };

function Health({ ok, label }) {
  return (
    <div className="flex items-center justify-between term-panel-2 rounded-sm px-3 py-2 text-xs">
      <span className="text-slate-300">{label}</span>
      <span className={`flex items-center gap-1.5 ${ok ? "text-[#00C805]" : "text-[#FF6b62]"}`}>
        <span className={`h-2 w-2 rounded-full ${ok ? "bg-[#00C805]" : "bg-[#FF3B30]"}`} />{ok ? "Healthy" : "Down"}
      </span>
    </div>
  );
}

export default function RiskCentre() {
  const [d, setD] = useState(null);
  const load = () => api.get("/risk").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); return visibleInterval(load, 5000); }, []);

  const kill = async () => { if (!window.confirm("EMERGENCY KILL SWITCH: square off everything and disable automation for the session?")) return;
    try { await api.post("/emergency/kill"); toast.warning("Kill switch activated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reactivate = async () => { try { await api.post("/emergency/reactivate"); toast.success("Automation reactivated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const exitAll = async () => { if (!window.confirm("Exit all & stop?")) return;
    try { await api.post("/broker/exit-all"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!d) return <div className="p-6 text-slate-500">Loading risk centre…</div>;
  const h = d.broker_health || {};

  return (
    <div className="p-6 space-y-4">
      {/* This page drives the development SIMULATION broker (one process-wide mock, ADMIN only).
          It does NOT control PAPER or LIVE trading — those controls live on Broker Session and
          are backed by the risk gate. Labelled so the two are never confused in an emergency. */}
      <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#FF6b62]" /> Risk Centre
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/40">SIMULATOR ONLY</span>
      </h1>
      <p className="text-[11px] text-slate-500 -mt-1">
        These controls act on the legacy simulation broker. PAPER and LIVE trading controls
        (stop new entries, kill switch, exit all) are on the Broker Session page.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="term-panel rounded-sm p-4"><div className="text-[11px] text-slate-500 uppercase">Margin Utilization</div><div className={`text-xl font-semibold mono mt-1 ${d.margin_util_pct > 70 ? "text-[#FF6b62]" : d.margin_util_pct > 50 ? "text-[#FFCC00]" : "text-slate-100"}`}>{d.margin_util_pct}%</div></div>
        <div className="term-panel rounded-sm p-4"><div className="text-[11px] text-slate-500 uppercase">Used Margin</div><div className="text-xl font-semibold mono mt-1">{inr(d.used_margin)}</div></div>
        <div className="term-panel rounded-sm p-4"><div className="text-[11px] text-slate-500 uppercase">Free Margin</div><div className="text-xl font-semibold mono mt-1">{inr(d.free_margin)}</div></div>
        <div className="term-panel rounded-sm p-4"><div className="text-[11px] text-slate-500 uppercase">Largest Position</div><div className="text-sm font-semibold mono mt-1">{d.largest_position ? `${d.largest_position.name}` : "—"}</div><div className="text-[10px] text-slate-500">{d.largest_position ? inr(d.largest_position.margin) : ""}</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Emergency controls */}
        <div className="term-panel rounded-sm p-5 space-y-3">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Emergency Controls</div>
          <button onClick={exitAll} className="w-full text-sm font-bold py-2.5 rounded-sm bg-[#FF3B30]/15 text-[#FF6b62] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25 flex items-center justify-center gap-2" data-testid="risk-exit-all"><Power className="h-4 w-4" /> EXIT ALL & STOP</button>
          {!d.kill_switch ? (
            <button onClick={kill} className="w-full text-sm font-bold py-2.5 rounded-sm bg-[#FF3B30] hover:bg-[#e0342a] text-white flex items-center justify-center gap-2" data-testid="risk-kill-switch"><Zap className="h-4 w-4" /> EMERGENCY KILL SWITCH</button>
          ) : (
            <button onClick={reactivate} className="w-full text-sm font-bold py-2.5 rounded-sm bg-[#2563EB] hover:bg-[#1d4fd0] flex items-center justify-center gap-2" data-testid="risk-reactivate"><RotateCcw className="h-4 w-4" /> REACTIVATE AUTOMATION</button>
          )}
          <div className={`text-xs px-3 py-2 rounded-sm ${d.kill_switch ? "bg-[#FF3B30]/15 text-[#FF6b62]" : "bg-[#1A1D24] text-slate-400"}`}>
            Kill switch: <b>{d.kill_switch ? "ACTIVE" : "inactive"}</b> · Automation: <b>{d.automation_enabled ? "enabled" : "disabled"}</b>
          </div>
        </div>

        {/* Broker health */}
        <div className="term-panel rounded-sm p-5 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">System Health</div>
          <Health ok={h.broker_connected} label="Broker Connection" />
          <Health ok={h.market_data_connected} label="Market Data Feed" />
          <Health ok={h.order_stream_connected} label="Order Stream" />
          <Health ok={h.automation_ready} label="Automation" />
        </div>

        {/* Positions risk */}
        <div className="term-panel rounded-sm p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Active Position Risk</div>
          <div className="space-y-2 text-xs max-h-56 overflow-auto">
            {d.strategies.length === 0 && <div className="text-slate-600">No active positions.</div>}
            {d.strategies.map((s) => (
              <div key={s.id} className="border-b border-[#20242c] pb-2">
                <div className="flex justify-between"><span className="font-semibold">{s.name}</span><span className={`mono ${pnlColor(s.net_pnl)}`}>{inr(s.net_pnl)}</span></div>
                <div className="flex justify-between text-slate-500 mono text-[11px]"><span>{s.total_matched_lots} lots · wavg ₹{num(s.weighted_avg_spread, 2)}</span><span>{s.days_to_near_expiry}d to expiry</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk events */}
      <div className="term-panel rounded-sm overflow-auto">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 px-4 py-3 border-b border-[#2A2F3A]">Risk Events</div>
        <table className="w-full text-xs">
          <thead className="bg-[#1A1D24] text-slate-400 text-left"><tr>{["Time", "Type", "Severity", "Details"].map((h2) => <th key={h2} className="px-3 py-2 font-semibold">{h2}</th>)}</tr></thead>
          <tbody>
            {d.events.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-600">No risk events recorded.</td></tr>}
            {d.events.map((e) => (
              <tr key={e.id} className="gz-row">
                <td className="px-3 py-2 mono text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleTimeString()}</td>
                <td className="px-3 py-2 mono">{e.event_type}</td>
                <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold ${SEV[e.severity] || SEV.low}`}>{e.severity}</span></td>
                <td className="px-3 py-2 text-slate-300">{e.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
